import type {
  ModelTier,
  ReasoningEffort,
  TaskType,
} from '../domain/types'

export type EvidenceAuthority =
  | 'official-guide'
  | 'official-model-card'
  | 'research'
  | 'practitioner'

export type RouteCard = {
  id: string
  title: string
  provider: string
  authority: EvidenceAuthority
  temporalScope: 'current' | 'historical' | 'methodology'
  sourceTitle: string
  sourceUrl: string
  sourceReviewedAt: string
  observation: string
  matchSignals: string[]
  taskTypes: TaskType[]
  mapping: {
    tier?: ModelTier
    effort?: ReasoningEffort
    status: 'official' | 'prototype-inference'
  }
  positiveExamples: string[]
  counterExamples: string[]
  exampleOrigin: 'synthetic-policy-example'
}

const reviewedAt = '2026-07-25'

export const ROUTE_CARDS: RouteCard[] = [
  {
    id: 'openai/luna-efficient-work',
    title: 'Clear, repeatable, high-volume work',
    provider: 'OpenAI',
    authority: 'official-model-card',
    temporalScope: 'current',
    sourceTitle: 'GPT-5.6 Luna model guidance',
    sourceUrl:
      'https://developers.openai.com/api/docs/models/gpt-5.6-luna',
    sourceReviewedAt: reviewedAt,
    observation:
      'Luna is the efficient GPT-5.6 tier for well-scoped, repeatable, high-volume work.',
    matchSignals: [
      'specific request',
      'bounded transformation',
      'high volume',
      'strict latency',
    ],
    taskTypes: ['extract', 'classify', 'rewrite'],
    mapping: {
      tier: 'direct',
      effort: 'low',
      status: 'official',
    },
    positiveExamples: [
      'Extract the invoice number and due date into the supplied JSON schema.',
    ],
    counterExamples: [
      'Investigate why invoices are intermittently duplicated across two payment systems.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'openai/terra-balanced-work',
    title: 'Everyday work that needs judgment',
    provider: 'OpenAI',
    authority: 'official-model-card',
    temporalScope: 'current',
    sourceTitle: 'GPT-5.6 Terra model guidance',
    sourceUrl:
      'https://developers.openai.com/api/docs/models/gpt-5.6-terra',
    sourceReviewedAt: reviewedAt,
    observation:
      'Terra balances intelligence, latency, and cost for ordinary professional work.',
    matchSignals: [
      'several related steps',
      'ordinary coding',
      'moderate judgment',
    ],
    taskTypes: ['implement', 'review', 'plan'],
    mapping: {
      tier: 'standard',
      effort: 'medium',
      status: 'official',
    },
    positiveExamples: [
      'Add a validated settings form, update the API client, and cover the behavior with tests.',
    ],
    counterExamples: [
      'Prove the source of a cross-region consistency failure under a production incident.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'openai/sol-frontier-work',
    title: 'Difficult, ambiguous, high-value work',
    provider: 'OpenAI',
    authority: 'official-model-card',
    temporalScope: 'current',
    sourceTitle: 'GPT-5.6 Sol model guidance',
    sourceUrl:
      'https://developers.openai.com/api/docs/models/gpt-5.6-sol',
    sourceReviewedAt: reviewedAt,
    observation:
      'Sol is the frontier GPT-5.6 tier for difficult professional work with a high accuracy bar.',
    matchSignals: [
      'deep ambiguity',
      'root-cause analysis',
      'system design',
      'high stakes',
    ],
    taskTypes: ['debug', 'research', 'plan'],
    mapping: {
      tier: 'deep',
      effort: 'high',
      status: 'official',
    },
    positiveExamples: [
      'Find the root cause of an intermittent authorization failure across refreshed browser sessions.',
    ],
    counterExamples: [
      'Rename one exported function and update its direct unit tests.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'openai/reasoning-low',
    title: 'Low effort for latency-sensitive work',
    provider: 'OpenAI',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'GPT-5.6 prompting guidance',
    sourceUrl:
      'https://developers.openai.com/api/docs/guides/prompt-guidance-gpt-5p6',
    sourceReviewedAt: reviewedAt,
    observation:
      'Low reasoning is appropriate when it preserves quality on latency-sensitive, well-scoped tasks.',
    matchSignals: [
      'clear output',
      'known pattern',
      'latency sensitive',
    ],
    taskTypes: ['extract', 'classify', 'rewrite'],
    mapping: {
      effort: 'low',
      status: 'official',
    },
    positiveExamples: [
      'Classify each support ticket into one of the five supplied labels.',
    ],
    counterExamples: [
      'Reconcile contradictory security evidence and recommend a containment plan.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'openai/reasoning-medium',
    title: 'Medium effort as a balanced starting point',
    provider: 'OpenAI',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'GPT-5.6 prompting guidance',
    sourceUrl:
      'https://developers.openai.com/api/docs/guides/prompt-guidance-gpt-5p6',
    sourceReviewedAt: reviewedAt,
    observation:
      'Medium reasoning is the balanced starting point for work that requires planning or judgment.',
    matchSignals: [
      'moderate planning',
      'several steps',
      'judgment',
    ],
    taskTypes: ['implement', 'review', 'plan'],
    mapping: {
      effort: 'medium',
      status: 'official',
    },
    positiveExamples: [
      'Plan and implement a small feature across the named component, hook, and tests.',
    ],
    counterExamples: [
      'Return the supplied records sorted by date as CSV.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'openai/reasoning-high',
    title: 'High effort only after a measured gain',
    provider: 'OpenAI',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'GPT-5.6 prompting guidance',
    sourceUrl:
      'https://developers.openai.com/api/docs/guides/prompt-guidance-gpt-5p6',
    sourceReviewedAt: reviewedAt,
    observation:
      'High and above should be reserved for hard quality-first work where evals show a meaningful gain.',
    matchSignals: [
      'hard debugging',
      'research synthesis',
      'difficult tradeoffs',
      'quality first',
    ],
    taskTypes: ['debug', 'research', 'plan'],
    mapping: {
      effort: 'high',
      status: 'official',
    },
    positiveExamples: [
      'Compare competing architectures, resolve source conflicts, and defend a production recommendation.',
    ],
    counterExamples: [
      'Rewrite this paragraph in plain language without changing any claims.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'openai/outcome-first',
    title: 'State the outcome and completion bar',
    provider: 'OpenAI',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'GPT-5.6 prompting guidance',
    sourceUrl:
      'https://developers.openai.com/api/docs/guides/prompt-guidance-gpt-5p6',
    sourceReviewedAt: reviewedAt,
    observation:
      'Current GPT-5.6 prompts benefit from an outcome, important constraints, evidence, and a clear completion bar.',
    matchSignals: [
      'success criteria',
      'required output',
      'constraints',
      'definition of done',
    ],
    taskTypes: ['other'],
    mapping: {
      status: 'official',
    },
    positiveExamples: [
      'Success means the migration preserves all routes, passes the named checks, and includes a rollback note.',
    ],
    counterExamples: [
      'Make the migration better and be comprehensive.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'openai/remove-scaffolding',
    title: 'Remove repeated prompt scaffolding',
    provider: 'OpenAI',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'GPT-5.6 prompting guidance',
    sourceUrl:
      'https://developers.openai.com/api/docs/guides/prompt-guidance-gpt-5p6',
    sourceReviewedAt: reviewedAt,
    observation:
      'Repeated instructions, irrelevant examples, and unused tools can reduce reliability and efficiency.',
    matchSignals: [
      'repeated rules',
      'conflicting instructions',
      'irrelevant tools',
      'generic style boilerplate',
    ],
    taskTypes: ['other'],
    mapping: {
      status: 'official',
    },
    positiveExamples: [
      'State each invariant once and expose only the tools needed for this task.',
    ],
    counterExamples: [
      'Repeat “be thorough” in every section and include all available tools.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'openai/stop-rules',
    title: 'Define stopping, fallback, and abstention rules',
    provider: 'OpenAI',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'GPT-5.6 prompting guidance',
    sourceUrl:
      'https://developers.openai.com/api/docs/guides/prompt-guidance-gpt-5p6',
    sourceReviewedAt: reviewedAt,
    observation:
      'Long or tool-heavy work should specify when to retry, fallback, ask, abstain, and stop.',
    matchSignals: [
      'retry limit',
      'fallback',
      'approval boundary',
      'stop condition',
    ],
    taskTypes: ['research', 'implement', 'plan'],
    mapping: {
      status: 'official',
    },
    positiveExamples: [
      'Retry transient failures twice; if the required source remains unavailable, name the gap and stop.',
    ],
    counterExamples: [
      'Keep searching until everything is perfect.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'openai/current-information',
    title: 'Retrieve current or source-grounded facts',
    provider: 'OpenAI',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'GPT-5.6 prompting guidance',
    sourceUrl:
      'https://developers.openai.com/api/docs/guides/prompt-guidance-gpt-5p6',
    sourceReviewedAt: reviewedAt,
    observation:
      'Current, obscure, or citation-sensitive facts require retrieval and explicit evidence handling.',
    matchSignals: [
      'latest information',
      'current facts',
      'citations',
      'source conflict',
    ],
    taskTypes: ['research', 'review'],
    mapping: {
      tier: 'deep',
      effort: 'high',
      status: 'prototype-inference',
    },
    positiveExamples: [
      'Verify the current provider model lineup from official docs and cite every mapping.',
    ],
    counterExamples: [
      'Convert this supplied timestamp to ISO 8601.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'openai/ptc-bounded-reduction',
    title: 'Programmatic tools for bounded reduction',
    provider: 'OpenAI',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'GPT-5.6 prompting guidance',
    sourceUrl:
      'https://developers.openai.com/api/docs/guides/prompt-guidance-gpt-5p6',
    sourceReviewedAt: reviewedAt,
    observation:
      'Filtering, joining, sorting, ranking, deduplication, and aggregation are good programmatic-tool candidates.',
    matchSignals: [
      'filter records',
      'join data',
      'deduplicate',
      'aggregate',
    ],
    taskTypes: ['extract', 'classify'],
    mapping: {
      tier: 'direct',
      effort: 'low',
      status: 'prototype-inference',
    },
    positiveExamples: [
      'Deduplicate these records by ID, sort them by date, and emit the fixed schema.',
    ],
    counterExamples: [
      'Choose a negotiation position after interpreting ambiguous stakeholder goals.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'openai/direct-tools-judgment',
    title: 'Direct tools for adaptive judgment',
    provider: 'OpenAI',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'GPT-5.6 prompting guidance',
    sourceUrl:
      'https://developers.openai.com/api/docs/guides/prompt-guidance-gpt-5p6',
    sourceReviewedAt: reviewedAt,
    observation:
      'Adaptive decisions, approvals, citations, and native artifacts should remain visible to direct model judgment.',
    matchSignals: [
      'approval',
      'adaptive workflow',
      'citations',
      'native artifact',
    ],
    taskTypes: ['research', 'plan', 'implement'],
    mapping: {
      tier: 'standard',
      effort: 'medium',
      status: 'prototype-inference',
    },
    positiveExamples: [
      'Research the sources, decide what is supported, and pause before any external write.',
    ],
    counterExamples: [
      'Sort this fixed array and return the first five rows.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'openai/avoid-chain-of-thought',
    title: 'Do not request hidden chain-of-thought',
    provider: 'OpenAI',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'Reasoning best practices',
    sourceUrl:
      'https://developers.openai.com/api/docs/guides/reasoning-best-practices',
    sourceReviewedAt: reviewedAt,
    observation:
      'Current reasoning models should receive direct goals and checks rather than requests to reveal step-by-step reasoning.',
    matchSignals: [
      'think step by step',
      'show your reasoning',
      'chain of thought',
    ],
    taskTypes: ['other'],
    mapping: {
      status: 'official',
    },
    positiveExamples: [
      'Give the answer, the decisive evidence, and the checks you ran.',
    ],
    counterExamples: [
      'Reveal every private reasoning step before answering.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'openai/historical-explicit-planning',
    title: 'Historical non-reasoning model scaffolding',
    provider: 'OpenAI',
    authority: 'official-guide',
    temporalScope: 'historical',
    sourceTitle: 'GPT-4.1 prompting guide',
    sourceUrl:
      'https://developers.openai.com/cookbook/examples/gpt4-1_prompting_guide',
    sourceReviewedAt: reviewedAt,
    observation:
      'GPT-4.1 guidance favored more explicit planning, persistence, and tool instructions than current reasoning-model guidance.',
    matchSignals: [
      'explicit intermediate steps',
      'literal procedure',
      'persistence boilerplate',
    ],
    taskTypes: ['implement', 'plan'],
    mapping: {
      status: 'official',
    },
    positiveExamples: [
      'For a pinned historical GPT-4.1 workflow, spell out the tool loop and intermediate plan.',
    ],
    counterExamples: [
      'Apply this historical pattern unchanged to every current reasoning model.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'anthropic/haiku-efficiency',
    title: 'Straightforward, high-volume Claude work',
    provider: 'Anthropic',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'Choosing a Claude model',
    sourceUrl:
      'https://platform.claude.com/docs/en/about-claude/models/choosing-a-model',
    sourceReviewedAt: reviewedAt,
    observation:
      'Anthropic recommends its efficient tier for straightforward, high-volume, latency- or cost-sensitive work.',
    matchSignals: [
      'straightforward',
      'high volume',
      'cost sensitive',
      'prototype',
    ],
    taskTypes: ['extract', 'classify', 'rewrite'],
    mapping: {
      tier: 'direct',
      effort: 'low',
      status: 'official',
    },
    positiveExamples: [
      'Normalize these product titles using the supplied rules and JSON schema.',
    ],
    counterExamples: [
      'Design a long-horizon autonomous migration across several systems.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'anthropic/sonnet-balance',
    title: 'Balanced Claude coding and agent work',
    provider: 'Anthropic',
    authority: 'official-model-card',
    temporalScope: 'current',
    sourceTitle: 'Claude model overview',
    sourceUrl:
      'https://platform.claude.com/docs/en/about-claude/models/overview',
    sourceReviewedAt: reviewedAt,
    observation:
      'Sonnet is positioned as the speed and intelligence balance for coding and agentic work.',
    matchSignals: [
      'coding',
      'agent tools',
      'several steps',
      'balanced quality',
    ],
    taskTypes: ['implement', 'review', 'plan'],
    mapping: {
      tier: 'standard',
      effort: 'medium',
      status: 'official',
    },
    positiveExamples: [
      'Implement the specified component behavior and verify it in the browser.',
    ],
    counterExamples: [
      'Resolve a novel high-stakes architectural dispute with sparse evidence.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'anthropic/opus-capability',
    title: 'Nuanced, accuracy-first Claude work',
    provider: 'Anthropic',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'Choosing a Claude model',
    sourceUrl:
      'https://platform.claude.com/docs/en/about-claude/models/choosing-a-model',
    sourceReviewedAt: reviewedAt,
    observation:
      'Anthropic positions Opus for complex reasoning, advanced coding, nuance, and accuracy-first work.',
    matchSignals: [
      'nuance',
      'advanced coding',
      'accuracy first',
      'complex reasoning',
    ],
    taskTypes: ['debug', 'research', 'plan'],
    mapping: {
      tier: 'deep',
      effort: 'high',
      status: 'official',
    },
    positiveExamples: [
      'Audit a security-sensitive architecture, reconcile conflicting evidence, and recommend mitigations.',
    ],
    counterExamples: [
      'Extract three fields from each supplied record.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'anthropic/fable-long-horizon',
    title: 'Long-running autonomous agent work',
    provider: 'Anthropic',
    authority: 'official-model-card',
    temporalScope: 'current',
    sourceTitle: 'Claude model overview',
    sourceUrl:
      'https://platform.claude.com/docs/en/about-claude/models/overview',
    sourceReviewedAt: reviewedAt,
    observation:
      'Fable is positioned for slower, long-running agents with always-on adaptive thinking.',
    matchSignals: [
      'long-running agent',
      'long horizon',
      'many dependent phases',
    ],
    taskTypes: ['research', 'implement', 'plan'],
    mapping: {
      tier: 'deep',
      effort: 'xhigh',
      status: 'official',
    },
    positiveExamples: [
      'Research, plan, implement, validate, and release a multi-system migration over many dependent phases.',
    ],
    counterExamples: [
      'Summarize this supplied paragraph in two bullets.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'anthropic/effort-before-model',
    title: 'Tune effort before changing model class',
    provider: 'Anthropic',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'Claude effort guidance',
    sourceUrl:
      'https://platform.claude.com/docs/en/build-with-claude/effort',
    sourceReviewedAt: reviewedAt,
    observation:
      'Anthropic notes that effort may be a better optimization lever than switching models.',
    matchSignals: [
      'quality regression',
      'latency tradeoff',
      'same model',
      'effort tuning',
    ],
    taskTypes: ['other'],
    mapping: {
      status: 'official',
    },
    positiveExamples: [
      'Baseline the current route, then test the same effort and one adjacent level.',
    ],
    counterExamples: [
      'Replace the model and rewrite the prompt simultaneously without an eval.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'anthropic/few-shot',
    title: 'Examples for subtle target behavior',
    provider: 'Anthropic',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'Claude prompting best practices',
    sourceUrl:
      'https://platform.claude.com/docs/en/build-with-claude/prompt-engineering/claude-prompting-best-practices',
    sourceReviewedAt: reviewedAt,
    observation:
      'Relevant, diverse, structured examples help when the target behavior or style is subtle.',
    matchSignals: [
      'subtle style',
      'few-shot',
      'edge cases',
      'target behavior',
    ],
    taskTypes: ['classify', 'rewrite', 'generate'],
    mapping: {
      status: 'official',
    },
    positiveExamples: [
      'Supply a small set of diverse accepted and rejected examples for this nuanced label.',
    ],
    counterExamples: [
      'Add unrelated examples to a simple extraction task that already passes.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'anthropic/structured-decision',
    title: 'Use a schema for machine-consumed decisions',
    provider: 'Anthropic',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'Claude tool use',
    sourceUrl:
      'https://platform.claude.com/docs/en/agents-and-tools/tool-use/how-tool-use-works',
    sourceReviewedAt: reviewedAt,
    observation:
      'Structured outputs or tools are preferable when a later system would otherwise parse free text.',
    matchSignals: [
      'fixed schema',
      'machine consumed',
      'JSON',
      'tool arguments',
    ],
    taskTypes: ['extract', 'classify'],
    mapping: {
      tier: 'direct',
      effort: 'low',
      status: 'prototype-inference',
    },
    positiveExamples: [
      'Return exactly the documented decision object with its enum and evidence fields.',
    ],
    counterExamples: [
      'Write a nuanced strategy memo for a human reader.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'google/simple-bounded',
    title: 'Specific work with one to three tool calls',
    provider: 'Google',
    authority: 'official-model-card',
    temporalScope: 'current',
    sourceTitle: 'Gemini 3.1 Flash-Lite routing example',
    sourceUrl:
      'https://ai.google.dev/gemini-api/docs/models/gemini-3.1-flash-lite',
    sourceReviewedAt: reviewedAt,
    observation:
      'Google’s routing example treats specific, bounded work with roughly one to three tool calls as simple.',
    matchSignals: [
      'specific',
      'bounded',
      'one to three tools',
      'known pattern',
    ],
    taskTypes: ['extract', 'classify', 'rewrite', 'implement'],
    mapping: {
      tier: 'direct',
      effort: 'low',
      status: 'prototype-inference',
    },
    positiveExamples: [
      'Open the named file, change the exact label, run its one focused test.',
    ],
    counterExamples: [
      'Explore the whole repository and decide what architecture should replace the current system.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'google/complex-multistep',
    title: 'Four or more steps, ambiguity, or root cause',
    provider: 'Google',
    authority: 'official-model-card',
    temporalScope: 'current',
    sourceTitle: 'Gemini 3.1 Flash-Lite routing example',
    sourceUrl:
      'https://ai.google.dev/gemini-api/docs/models/gemini-3.1-flash-lite',
    sourceReviewedAt: reviewedAt,
    observation:
      'Google’s routing example treats four or more tool calls, strategic planning, broad scope, ambiguity, and deep debugging as complex.',
    matchSignals: [
      'four or more tools',
      'strategic planning',
      'broad scope',
      'root cause',
    ],
    taskTypes: ['debug', 'research', 'plan'],
    mapping: {
      tier: 'deep',
      effort: 'high',
      status: 'prototype-inference',
    },
    positiveExamples: [
      'Trace the failure across several services, compare hypotheses, test the fix, and prepare a rollout.',
    ],
    counterExamples: [
      'Convert these known values into the provided YAML template.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'google/minimal-thinking',
    title: 'Minimal thinking for deterministic execution',
    provider: 'Google',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'Gemini thinking levels',
    sourceUrl:
      'https://ai.google.dev/gemini-api/docs/generate-content/thinking',
    sourceReviewedAt: reviewedAt,
    observation:
      'Minimal thinking targets near-no-deliberation work where latency and cost dominate.',
    matchSignals: [
      'deterministic',
      'straightforward',
      'minimal latency',
    ],
    taskTypes: ['extract', 'classify', 'rewrite'],
    mapping: {
      effort: 'none',
      status: 'official',
    },
    positiveExamples: [
      'Parse the supplied identifiers and return them in the fixed order.',
    ],
    counterExamples: [
      'Evaluate uncertain evidence with material safety consequences.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'google/medium-thinking',
    title: 'Medium thinking for most complicated tasks',
    provider: 'Google',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'Gemini thinking levels',
    sourceUrl:
      'https://ai.google.dev/gemini-api/docs/generate-content/thinking',
    sourceReviewedAt: reviewedAt,
    observation:
      'Medium thinking balances quality and speed for complicated code and agent work.',
    matchSignals: [
      'complicated code',
      'agent task',
      'balanced',
    ],
    taskTypes: ['implement', 'review', 'plan'],
    mapping: {
      effort: 'medium',
      status: 'official',
    },
    positiveExamples: [
      'Make the related code edits, use the available tools, and verify the behavior.',
    ],
    counterExamples: [
      'Return a single known field from each row.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'google/high-thinking',
    title: 'High thinking for hardest reasoning and coding',
    provider: 'Google',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'Gemini thinking levels',
    sourceUrl:
      'https://ai.google.dev/gemini-api/docs/generate-content/thinking',
    sourceReviewedAt: reviewedAt,
    observation:
      'High thinking is intended for the hardest reasoning and coding work, with slower response as a tradeoff.',
    matchSignals: [
      'hardest reasoning',
      'hard coding',
      'quality first',
    ],
    taskTypes: ['debug', 'research', 'plan'],
    mapping: {
      effort: 'high',
      status: 'official',
    },
    positiveExamples: [
      'Diagnose a non-deterministic concurrency defect and prove the fix under adversarial tests.',
    ],
    counterExamples: [
      'Translate these five labels using the supplied glossary.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'google/search-grounding',
    title: 'Ground recent or obscure facts',
    provider: 'Google',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'Gemini prompting strategies',
    sourceUrl:
      'https://ai.google.dev/gemini-api/docs/prompting-strategies',
    sourceReviewedAt: reviewedAt,
    observation:
      'Recent or obscure information should use search grounding rather than model memory alone.',
    matchSignals: [
      'current',
      'recent',
      'obscure',
      'verify online',
    ],
    taskTypes: ['research', 'review'],
    mapping: {
      tier: 'standard',
      effort: 'medium',
      status: 'prototype-inference',
    },
    positiveExamples: [
      'Check today’s official release notes and explain the current supported behavior.',
    ],
    counterExamples: [
      'Summarize the complete source text already included below.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'google/code-execution',
    title: 'Use code for deterministic calculation',
    provider: 'Google',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'Gemini prompting strategies',
    sourceUrl:
      'https://ai.google.dev/gemini-api/docs/prompting-strategies',
    sourceReviewedAt: reviewedAt,
    observation:
      'Arithmetic, counting, and deterministic calculations should use code execution when available.',
    matchSignals: [
      'calculate',
      'count',
      'aggregate',
      'exact arithmetic',
    ],
    taskTypes: ['extract', 'other'],
    mapping: {
      tier: 'direct',
      effort: 'low',
      status: 'prototype-inference',
    },
    positiveExamples: [
      'Calculate the exact percentile values from this supplied dataset with code.',
    ],
    counterExamples: [
      'Interpret which of two product strategies is more defensible.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'google/long-context-question-last',
    title: 'Put long source material before the question',
    provider: 'Google',
    authority: 'official-guide',
    temporalScope: 'current',
    sourceTitle: 'Gemini prompting strategies',
    sourceUrl:
      'https://ai.google.dev/gemini-api/docs/prompting-strategies',
    sourceReviewedAt: reviewedAt,
    observation:
      'For long-context prompts, source material should precede the question and the answer should be anchored to that material.',
    matchSignals: [
      'long documents',
      'source corpus',
      'question last',
      'grounded synthesis',
    ],
    taskTypes: ['research', 'review'],
    mapping: {
      status: 'official',
    },
    positiveExamples: [
      'Provide the source packet first, then ask the exact comparison question and required citations.',
    ],
    counterExamples: [
      'Hide the question between unrelated source fragments.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'routellm/cheapest-sufficient',
    title: 'Route to the cheapest model that meets the bar',
    provider: 'RouteLLM',
    authority: 'research',
    temporalScope: 'methodology',
    sourceTitle: 'RouteLLM',
    sourceUrl: 'https://github.com/lm-sys/RouteLLM',
    sourceReviewedAt: reviewedAt,
    observation:
      'Strong-versus-weak routing must be calibrated on the target query distribution and quality threshold.',
    matchSignals: [
      'cost quality tradeoff',
      'threshold calibration',
      'representative eval',
    ],
    taskTypes: ['other'],
    mapping: {
      status: 'prototype-inference',
    },
    positiveExamples: [
      'Choose the lowest-cost route that still passes the frozen acceptance rubric.',
    ],
    counterExamples: [
      'Assume one global threshold transfers unchanged to every model pair and workload.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'vllm/multiple-signals',
    title: 'Compose semantic and explicit signals',
    provider: 'vLLM Semantic Router',
    authority: 'practitioner',
    temporalScope: 'methodology',
    sourceTitle: 'vLLM Semantic Router',
    sourceUrl:
      'https://github.com/vllm-project/semantic-router',
    sourceReviewedAt: reviewedAt,
    observation:
      'Production routers combine task, complexity, tools, context, safety, and policy signals rather than relying on one similarity score.',
    matchSignals: [
      'hybrid router',
      'tools',
      'context',
      'safety policy',
    ],
    taskTypes: ['other'],
    mapping: {
      status: 'prototype-inference',
    },
    positiveExamples: [
      'Use semantic matches as evidence while explicit risk and tool requirements determine the route.',
    ],
    counterExamples: [
      'Choose the model from the nearest embedding alone.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'semantic-router/abstention',
    title: 'Allow no confident match',
    provider: 'Aurelio Semantic Router',
    authority: 'practitioner',
    temporalScope: 'methodology',
    sourceTitle: 'Semantic Router',
    sourceUrl:
      'https://github.com/aurelio-labs/semantic-router',
    sourceReviewedAt: reviewedAt,
    observation:
      'Similarity routing should use per-route thresholds and preserve an explicit no-match outcome.',
    matchSignals: [
      'low similarity',
      'close margin',
      'unknown distribution',
      'abstain',
    ],
    taskTypes: ['other'],
    mapping: {
      status: 'prototype-inference',
    },
    positiveExamples: [
      'Say that evidence is insufficient when the nearest examples are weak or contradictory.',
    ],
    counterExamples: [
      'Always force the nearest route even when every similarity is low.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'nvidia/complexity-dimensions',
    title: 'Keep complexity dimensions separate',
    provider: 'NVIDIA',
    authority: 'official-model-card',
    temporalScope: 'methodology',
    sourceTitle: 'Prompt task and complexity classifier',
    sourceUrl:
      'https://huggingface.co/nvidia/prompt-task-and-complexity-classifier',
    sourceReviewedAt: reviewedAt,
    observation:
      'NVIDIA’s comparator separates creativity, reasoning, context, domain knowledge, constraints, and few-shot needs.',
    matchSignals: [
      'multiple dimensions',
      'domain knowledge',
      'constraints',
      'reasoning',
    ],
    taskTypes: ['other'],
    mapping: {
      status: 'prototype-inference',
    },
    positiveExamples: [
      'Report task demand and prompt readiness separately instead of one opaque intelligence score.',
    ],
    counterExamples: [
      'Collapse every signal into an unexplained 73 percent complexity badge.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'routerbench/baselines',
    title: 'Compare against simple and oracle baselines',
    provider: 'RouterBench',
    authority: 'research',
    temporalScope: 'methodology',
    sourceTitle: 'RouterBench',
    sourceUrl:
      'https://github.com/withmartian/routerbench',
    sourceReviewedAt: reviewedAt,
    observation:
      'Router evaluation should compare learned routing with best-single, random, and oracle cost-quality baselines.',
    matchSignals: [
      'best single',
      'random baseline',
      'oracle',
      'cost quality frontier',
    ],
    taskTypes: ['other'],
    mapping: {
      status: 'prototype-inference',
    },
    positiveExamples: [
      'Report whether the router beats always using the best single affordable model.',
    ],
    counterExamples: [
      'Declare success from aggregate accuracy without any routing baseline.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'promptfoo/model-comparison',
    title: 'Run prompt-by-model evaluations',
    provider: 'Promptfoo',
    authority: 'practitioner',
    temporalScope: 'methodology',
    sourceTitle: 'Promptfoo',
    sourceUrl: 'https://github.com/promptfoo/promptfoo',
    sourceReviewedAt: reviewedAt,
    observation:
      'Representative prompts should be run against candidate models with task-specific assertions, latency, tokens, and cost.',
    matchSignals: [
      'model comparison',
      'assertions',
      'latency',
      'cost',
    ],
    taskTypes: ['other'],
    mapping: {
      status: 'prototype-inference',
    },
    positiveExamples: [
      'Run each frozen task across the candidate routes and grade the actual outputs.',
    ],
    counterExamples: [
      'Ask one frontier model whether the router labels look plausible.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
  {
    id: 'evals/error-analysis',
    title: 'Calibrate from observed failures',
    provider: 'Practitioner eval guidance',
    authority: 'practitioner',
    temporalScope: 'methodology',
    sourceTitle: 'Hamel Husain eval skills',
    sourceUrl: 'https://github.com/hamelsmu/evals-skills',
    sourceReviewedAt: reviewedAt,
    observation:
      'Useful eval work starts with error analysis, human annotation, and calibration of automated judges.',
    matchSignals: [
      'error analysis',
      'human review',
      'judge calibration',
      'failure taxonomy',
    ],
    taskTypes: ['other'],
    mapping: {
      status: 'prototype-inference',
    },
    positiveExamples: [
      'Review false downgrades, label their failure modes, then change one policy rule.',
    ],
    counterExamples: [
      'Tune thresholds on the same examples used to report final accuracy.',
    ],
    exampleOrigin: 'synthetic-policy-example',
  },
]

export function routeCardEmbeddingText(card: RouteCard): string {
  return [
    card.title,
    card.observation,
    `Signals: ${card.matchSignals.join(', ')}`,
    `Examples: ${card.positiveExamples.join(' ')}`,
    `Counterexamples: ${card.counterExamples.join(' ')}`,
  ].join('\n')
}

export function findRouteCard(id: string): RouteCard | undefined {
  return ROUTE_CARDS.find((card) => card.id === id)
}
